import React, { useState, useEffect } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';

interface VerificationStatus {
  primary_email: boolean;
  secondary_email: boolean;
  mobile: boolean;
}

type VerificationChannel = keyof VerificationStatus;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
const TELEPHONE_REGEX = /^[+]?[\d\s\-()]{7,20}$/;

const UpdateDetails: React.FC = () => {
  const { convexUser } = useBetterAuthConvexAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    email_2: '',
    mobile_number: '',
    telephone_number: '',
    address: ''
  });

  const [verificationNeeded, setVerificationNeeded] = useState<VerificationStatus>({
    primary_email: false,
    secondary_email: false,
    mobile: false
  });

  const [verificationCodes, setVerificationCodes] = useState({
    primary_email: '',
    secondary_email: '',
    mobile: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sendingCode, setSendingCode] = useState({
    primary_email: false,
    secondary_email: false,
    mobile: false
  });

  // Convex mutations
  const updateProfile = useMutation(api.users.updateMyProfile);
  const sendVerificationCode = useMutation(api.users.sendMyVerificationCode);
  const verifyCode = useMutation(api.users.verifyMyCode);

  useEffect(() => {
    if (convexUser) {
      const userData = {
        name: convexUser.name || '',
        email: convexUser.email || '',
        email_2: convexUser.email2 || '',
        mobile_number: convexUser.mobileNumber || '',
        telephone_number: convexUser.telephoneNumber || '',
        address: convexUser.address || ''
      };
      setFormData(userData);
    }
  }, [convexUser]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getChannelLabel = (type: VerificationChannel) => {
    if (type === 'primary_email') return 'primary email';
    if (type === 'secondary_email') return 'secondary email';
    return 'mobile number';
  };

  const handleSendVerificationCode = async (type: VerificationChannel) => {
    setSendingCode(prev => ({ ...prev, [type]: true }));
    setMessage({ type: '', text: '' });

    try {
      const result = await sendVerificationCode({ type, clientSource: 'web' });

      if (result.success) {
        setMessage({
          type: 'success',
          text: `Verification code sent to your ${getChannelLabel(type)}`
        });
      } else {
        setMessage({
          type: 'error',
          text: result.error || `Failed to send ${getChannelLabel(type)} verification code`
        });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.message || `Failed to send ${getChannelLabel(type)} verification code`
      });
    } finally {
      setSendingCode(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleVerifyCode = async (type: VerificationChannel) => {
    const code = verificationCodes[type];

    if (code.length !== 4) {
      setMessage({ type: 'error', text: 'Please enter a 4-digit code' });
      return;
    }

    setLoading(true);
    try {
      const result = await verifyCode({ type, code });

      if (result.success) {
        // Update verification status
        setVerificationNeeded(prev => ({ ...prev, [type]: false }));
        setVerificationCodes(prev => ({ ...prev, [type]: '' }));

        setMessage({
          type: 'success',
          text: result.pendingApproval
            ? 'OTP verified. Approval emails sent. Your profile will update once approved.'
            : `${getChannelLabel(type)} OTP verified. Verify the remaining channel if needed.`
        });
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Invalid verification code'
        });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.message || 'Invalid verification code'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const wrongInput: string[] = [];
      const email1 = formData.email.trim().toLowerCase();
      const email2 = formData.email_2.trim().toLowerCase();
      const mobileNumber = formData.mobile_number.trim();
      const telephoneNumber = formData.telephone_number.trim();

      if (email2 && !EMAIL_REGEX.test(email2)) {
        wrongInput.push('Enter Valid Email ID');
      }
      if (mobileNumber && !MOBILE_REGEX.test(mobileNumber)) {
        wrongInput.push('Enter Valid Mobile Number');
      }
      if (telephoneNumber && !TELEPHONE_REGEX.test(telephoneNumber)) {
        wrongInput.push('Enter Valid Telephone Number');
      }
      if ((email1 && email2) && email1 === email2) {
        wrongInput.push("Primary and Secondary email addresses must be unique.");
      }

      if (wrongInput.length > 0) {
        setMessage({ type: 'error', text: wrongInput.map((m, i) => `${i + 1}. ${m}`).join('\n') });
        return;
      }

      const result = await updateProfile({
        name: formData.name || undefined,
        email: formData.email || undefined,
        email2: formData.email_2 || undefined,
        mobileNumber: formData.mobile_number || undefined,
        telephoneNumber: formData.telephone_number || undefined,
        address: formData.address || undefined,
      });

      if (result.success) {
        const nextVerification: VerificationStatus = {
          primary_email: !!result?.verificationNeeded?.primary_email,
          secondary_email: !!result?.verificationNeeded?.secondary_email,
          mobile: !!result?.verificationNeeded?.mobile,
        };
        setVerificationNeeded(nextVerification);

        if (!nextVerification.primary_email && !nextVerification.secondary_email && !nextVerification.mobile) {
          setMessage({ type: 'success', text: 'Details submitted successfully.' });
          return;
        }

        if (nextVerification.primary_email) {
          await sendVerificationCode({ type: 'primary_email', clientSource: 'web' });
        }
        if (nextVerification.secondary_email) {
          await sendVerificationCode({ type: 'secondary_email', clientSource: 'web' });
        }
        if (nextVerification.mobile) {
          await sendVerificationCode({ type: 'mobile', clientSource: 'web' });
        }

        setMessage({ type: 'success', text: 'OTP sent. Verify your code(s) to submit for approval.' });
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to update details'
        });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.message || 'Failed to update details'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-medium font-sans mb-4">Update Your Details</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary Email
            </label>
            <div className="space-y-2">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
              />
              {verificationNeeded.primary_email && (
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={verificationCodes.primary_email}
                    onChange={(e) => setVerificationCodes(prev => ({ ...prev, primary_email: e.target.value }))}
                    placeholder="Enter 4-digit code"
                    maxLength={4}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => handleSendVerificationCode('primary_email')}
                    disabled={sendingCode.primary_email}
                    className="px-4 py-2 bg-[#16A34A]  text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
                  >
                    {sendingCode.primary_email ? 'Sending...' : 'Send Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerifyCode('primary_email')}
                    disabled={verificationCodes.primary_email.length !== 4}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Verify
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Secondary Contact Email (Optional)
            </label>
            <input
              type="email"
              name="email_2"
              value={formData.email_2}
              onChange={handleInputChange}
              placeholder="secondary@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
            />
            {verificationNeeded.secondary_email && (
              <div className="flex space-x-2 mt-2">
                <input
                  type="text"
                  value={verificationCodes.secondary_email}
                  onChange={(e) => setVerificationCodes(prev => ({ ...prev, secondary_email: e.target.value }))}
                  placeholder="Enter 4-digit code"
                  maxLength={4}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => handleSendVerificationCode('secondary_email')}
                  disabled={sendingCode.secondary_email}
                  className="px-4 py-2 bg-[#16A34A]  text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
                >
                  {sendingCode.secondary_email ? 'Sending...' : 'Send Code'}
                </button>
                <button
                  type="button"
                  onClick={() => handleVerifyCode('secondary_email')}
                  disabled={verificationCodes.secondary_email.length !== 4}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number
            </label>
            <div className="space-y-2">
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder="07700123456"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
              />
              {verificationNeeded.mobile && (
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={verificationCodes.mobile}
                    onChange={(e) => setVerificationCodes(prev => ({ ...prev, mobile: e.target.value }))}
                    placeholder="Enter 4-digit code"
                    maxLength={4}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => handleSendVerificationCode('mobile')}
                    disabled={sendingCode.mobile}
                    className="px-4 py-2 bg-[#16A34A]  text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
                  >
                    {sendingCode.mobile ? 'Sending...' : 'Send SMS'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerifyCode('mobile')}
                    disabled={verificationCodes.mobile.length !== 4}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Verify
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telephone Number
            </label>
            <input
              type="tel"
              name="telephone_number"
              value={formData.telephone_number}
              onChange={handleInputChange}
              placeholder="01234567890"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
          />
        </div>

        {message.text && (
          <div
            className={`whitespace-pre-line p-3 rounded-md ${message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
              }`}
          >
            {message.type === 'success'
              ? message.text
              : `Please fix the following:\n\n${message.text}`}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={
              loading ||
              verificationNeeded.primary_email ||
              verificationNeeded.secondary_email ||
              verificationNeeded.mobile
            }
            className="px-6 py-2 bg-[#16A34A]  text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Details'}
          </button>
        </div>
      </form>

      <div className="mt-6 text-sm text-gray-600">
        <p className="font-medium font-sans mb-2">Verification Status:</p>
        <ul className="space-y-1">
          <li>
            Email: {convexUser?.emailVerified ? (
              <span className="text-green-600">✓ Verified</span>
            ) : (
              <span className="text-red-600">✗ Not verified</span>
            )}
          </li>
          <li>
            Mobile: {convexUser?.mobileVerified ? (
              <span className="text-green-600">✓ Verified</span>
            ) : (
              <span className="text-red-600">✗ Not verified</span>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default UpdateDetails;
